"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/**
 * The unit converter — the largest interactive surface among the first eight tools, and
 * the reason the model puts a tool's own strings with its component rather than in the
 * content file.
 *
 * Three things here cannot be prose. The section heading is computed from state
 * ("Common *length* conversions"), so it is a template with a placeholder rather than a
 * `h2` block. The thirty-seven unit labels are keyed by id and read back out of the
 * conversion result, so they are a map, not a list. And the six temperature formulas are
 * chosen by the pair of units selected. All three are overridable per locale through
 * `ui`, and all three would be unreachable if the strings had been left inline.
 */

type CategoryId =
  | "length"
  | "mass"
  | "temperature"
  | "volume"
  | "area"
  | "speed";

interface Unit {
  id: string;
  /** How many base units one of this unit equals (unused for temperature). */
  toBase: number;
}

interface CommonRow {
  value: number;
  from: string;
  to: string;
}

interface CategoryDef {
  id: CategoryId;
  units: Unit[];
  defaultFrom: string;
  defaultTo: string;
  common: CommonRow[];
}

const CATEGORIES: CategoryDef[] = [
  {
    id: "length",
    units: [
      { id: "mm", toBase: 0.001 },
      { id: "cm", toBase: 0.01 },
      { id: "m", toBase: 1 },
      { id: "km", toBase: 1000 },
      { id: "in", toBase: 0.0254 },
      { id: "ft", toBase: 0.3048 },
      { id: "yd", toBase: 0.9144 },
      { id: "mi", toBase: 1609.344 },
    ],
    defaultFrom: "cm",
    defaultTo: "in",
    common: [
      { value: 1, from: "in", to: "cm" },
      { value: 1, from: "ft", to: "m" },
      { value: 1, from: "yd", to: "m" },
      { value: 1, from: "mi", to: "km" },
      { value: 1, from: "m", to: "ft" },
      { value: 100, from: "km", to: "mi" },
    ],
  },
  {
    id: "mass",
    units: [
      { id: "mg", toBase: 0.000_001 },
      { id: "g", toBase: 0.001 },
      { id: "kg", toBase: 1 },
      { id: "t", toBase: 1000 },
      { id: "oz", toBase: 0.028_349_523_125 },
      { id: "lb", toBase: 0.453_592_37 },
      { id: "st", toBase: 6.350_293_18 },
    ],
    defaultFrom: "kg",
    defaultTo: "lb",
    common: [
      { value: 1, from: "oz", to: "g" },
      { value: 1, from: "lb", to: "kg" },
      { value: 1, from: "st", to: "kg" },
      { value: 1, from: "kg", to: "lb" },
      { value: 100, from: "g", to: "oz" },
      { value: 1, from: "t", to: "lb" },
    ],
  },
  {
    id: "temperature",
    units: [
      { id: "c", toBase: 1 },
      { id: "f", toBase: 1 },
      { id: "k", toBase: 1 },
    ],
    defaultFrom: "c",
    defaultTo: "f",
    common: [
      { value: 0, from: "c", to: "f" },
      { value: 100, from: "c", to: "f" },
      { value: 98.6, from: "f", to: "c" },
      { value: 37, from: "c", to: "f" },
      { value: 0, from: "c", to: "k" },
      { value: 300, from: "k", to: "c" },
    ],
  },
  {
    id: "volume",
    units: [
      { id: "ml", toBase: 0.001 },
      { id: "l", toBase: 1 },
      { id: "tsp", toBase: 0.004_928_921_593_75 },
      { id: "tbsp", toBase: 0.014_786_764_781_25 },
      { id: "cup", toBase: 0.236_588_236_5 },
      { id: "pt", toBase: 0.473_176_473 },
      { id: "qt", toBase: 0.946_352_946 },
      { id: "gal", toBase: 3.785_411_784 },
    ],
    defaultFrom: "l",
    defaultTo: "gal",
    common: [
      { value: 1, from: "cup", to: "ml" },
      { value: 1, from: "tbsp", to: "ml" },
      { value: 1, from: "gal", to: "l" },
      { value: 1, from: "l", to: "cup" },
      { value: 1, from: "pt", to: "ml" },
      { value: 2, from: "l", to: "gal" },
    ],
  },
  {
    id: "area",
    units: [
      { id: "cm2", toBase: 0.0001 },
      { id: "m2", toBase: 1 },
      { id: "ha", toBase: 10_000 },
      { id: "km2", toBase: 1_000_000 },
      { id: "in2", toBase: 0.000_645_16 },
      { id: "ft2", toBase: 0.092_903_04 },
      { id: "acre", toBase: 4046.856_422_4 },
      { id: "mi2", toBase: 2_589_988.110_336 },
    ],
    defaultFrom: "m2",
    defaultTo: "ft2",
    common: [
      { value: 1, from: "m2", to: "ft2" },
      { value: 1, from: "acre", to: "m2" },
      { value: 1, from: "ha", to: "acre" },
      { value: 1, from: "km2", to: "mi2" },
      { value: 100, from: "ft2", to: "m2" },
      { value: 1, from: "mi2", to: "km2" },
    ],
  },
  {
    id: "speed",
    units: [
      { id: "ms", toBase: 1 },
      { id: "kmh", toBase: 1 / 3.6 },
      { id: "mph", toBase: 0.447_04 },
      { id: "kn", toBase: 1852 / 3600 },
      { id: "fts", toBase: 0.3048 },
    ],
    defaultFrom: "kmh",
    defaultTo: "mph",
    common: [
      { value: 100, from: "kmh", to: "mph" },
      { value: 60, from: "mph", to: "kmh" },
      { value: 1, from: "kn", to: "kmh" },
      { value: 1, from: "ms", to: "mph" },
      { value: 30, from: "mph", to: "ms" },
      { value: 10, from: "kn", to: "mph" },
    ],
  },
];

/** English strings. A translated content file overrides these through `ui`. */
export const UNIT_CONVERTER_STRINGS = {
  category: "Category",
  value: "Value",
  from: "From",
  to: "To",
  swap: "⇄ Swap",
  swapLabel: "Swap units",
  result: "Result",
  invalid: "Enter a valid number to convert.",
  belowAbsoluteZero:
    "That temperature is below absolute zero (0 K, −273.15 °C, −459.67 °F), so it cannot be converted.",
  /** `{category}` is `categoryLabels`' sibling below, already in sentence form. */
  commonHeading: "Common {category} conversions",
  headFrom: "From",
  headTo: "To",
  sameScale: "Same scale — no conversion needed.",
  categoryLabels: {
    length: "Length",
    mass: "Mass",
    temperature: "Temperature",
    volume: "Volume",
    area: "Area",
    speed: "Speed",
  },
  /**
   * The same six, as they read inside a sentence — "Common length conversions".
   *
   * A second map rather than `categoryLabels[id].toLowerCase()`, which is what this
   * heading used to do. Case-folding a *translated* noun is wrong before Turkish is
   * even involved: German and Dutch capitalise every noun, so "Länge" became "länge"
   * in the middle of a German heading, and in Japanese and Chinese the operation has
   * no meaning at all. Six strings remove the computation entirely and let each
   * translator write the form their language actually uses — which for German is the
   * same word again, and for Turkish avoids the `ı`/`i` fold altogether.
   *
   * Until a locale supplies these, `inSentence()` below folds that locale's display
   * label as the page always did. That fallback is transitional and deliberately not
   * the default: dropping straight through to these English words would have put
   * "Conversiones habituales de length" on the Spanish page, which is worse than the
   * fold it replaces.
   */
  categoryLabelsInSentence: {
    length: "length",
    mass: "mass",
    temperature: "temperature",
    volume: "volume",
    area: "area",
    speed: "speed",
  },
  baseNotes: {
    length: "base unit: meter",
    mass: "base unit: kilogram",
    temperature: "converted by formula, not by factor",
    volume: "base unit: liter; cups, pints, quarts & gallons are US",
    area: "base unit: square meter",
    speed: "base unit: meter per second",
  },
  unitLabels: {
    mm: "Millimeters (mm)",
    cm: "Centimeters (cm)",
    m: "Meters (m)",
    km: "Kilometers (km)",
    in: "Inches (in)",
    ft: "Feet (ft)",
    yd: "Yards (yd)",
    mi: "Miles (mi)",
    mg: "Milligrams (mg)",
    g: "Grams (g)",
    kg: "Kilograms (kg)",
    t: "Tonnes (t)",
    oz: "Ounces (oz)",
    lb: "Pounds (lb)",
    st: "Stone (st)",
    c: "Celsius (°C)",
    f: "Fahrenheit (°F)",
    k: "Kelvin (K)",
    ml: "Milliliters (ml)",
    l: "Liters (l)",
    tsp: "Teaspoons US (tsp)",
    tbsp: "Tablespoons US (tbsp)",
    cup: "Cups US (cup)",
    pt: "Pints US (pt)",
    qt: "Quarts US (qt)",
    gal: "Gallons US (gal)",
    cm2: "Square centimeters (cm²)",
    m2: "Square meters (m²)",
    ha: "Hectares (ha)",
    km2: "Square kilometers (km²)",
    in2: "Square inches (in²)",
    ft2: "Square feet (ft²)",
    acre: "Acres",
    mi2: "Square miles (mi²)",
    ms: "Meters per second (m/s)",
    kmh: "Kilometers per hour (km/h)",
    mph: "Miles per hour (mph)",
    kn: "Knots (kn)",
    fts: "Feet per second (ft/s)",
  } as Record<string, string>,
  temperatureFormulas: {
    "c>f": "°F = °C × 9/5 + 32",
    "f>c": "°C = (°F − 32) × 5/9",
    "c>k": "K = °C + 273.15",
    "k>c": "°C = K − 273.15",
    "f>k": "K = (°F − 32) × 5/9 + 273.15",
    "k>f": "°F = (K − 273.15) × 9/5 + 32",
  } as Record<string, string>,
};

type Strings = typeof UNIT_CONVERTER_STRINGS;

function toCelsius(value: number, unit: string): number {
  if (unit === "f") {
    return ((value - 32) * 5) / 9;
  }
  if (unit === "k") {
    return value - 273.15;
  }
  return value;
}

function fromCelsius(celsius: number, unit: string): number {
  if (unit === "f") {
    return (celsius * 9) / 5 + 32;
  }
  if (unit === "k") {
    return celsius + 273.15;
  }
  return celsius;
}

/** Slack for the float error in (°F − 32) × 5/9, so −459.67 °F still counts as absolute zero. */
const ABSOLUTE_ZERO_TOLERANCE_C = 1e-9;

/** True when a temperature in °C, °F or K lies below absolute zero (0 K = −273.15 °C = −459.67 °F). */
export function isBelowAbsoluteZero(unitId: string, value: number): boolean {
  return toCelsius(value, unitId) < -273.15 - ABSOLUTE_ZERO_TOLERANCE_C;
}

function findCategory(id: CategoryId): CategoryDef {
  return CATEGORIES.find((c) => c.id === id) ?? CATEGORIES[0];
}

function findUnit(category: CategoryDef, id: string): Unit {
  return category.units.find((u) => u.id === id) ?? category.units[0];
}

function unitLabel(strings: Strings, id: string): string {
  return strings.unitLabels[id] ?? id;
}

/** The abbreviation inside the label's parentheses: "Meters (m)" → "m". */
function shortLabel(strings: Strings, id: string): string {
  const label = unitLabel(strings, id);
  const open = label.indexOf("(");
  if (open === -1) {
    return label;
  }
  return label.slice(open + 1, label.length - 1);
}

function convertValue(
  category: CategoryDef,
  fromId: string,
  toId: string,
  value: number
): number {
  if (category.id === "temperature") {
    return fromCelsius(toCelsius(value, fromId), toId);
  }
  const from = findUnit(category, fromId);
  const to = findUnit(category, toId);
  return (value * from.toBase) / to.toBase;
}

/**
 * A converted quantity, written the reader's way.
 *
 * `Number(n.toPrecision(6)).toString()` kept six significant figures and dropped the
 * trailing zeros, and then emitted a period regardless of language — so the German page
 * answered `0.393701 in`. `maximumSignificantDigits` is the same rounding and the same
 * trailing-zero behaviour through Intl. The formatter is a parameter because this helper
 * is module level and is called from `describeConversion`, which has no locale either.
 *
 * Display only: nothing here reaches an input, a URL or the clipboard.
 */
function formatSig(n: number, format: NumberFormatter): string {
  if (!Number.isFinite(n)) {
    return "—";
  }
  return format(n, { maximumSignificantDigits: 6 });
}

function describeConversion(
  strings: Strings,
  category: CategoryDef,
  fromId: string,
  toId: string,
  format: NumberFormatter
): string {
  if (category.id === "temperature") {
    if (fromId === toId) {
      return strings.sameScale;
    }
    return strings.temperatureFormulas[`${fromId}>${toId}`] ?? "";
  }
  const from = findUnit(category, fromId);
  const to = findUnit(category, toId);
  const factor = from.toBase / to.toBase;
  return `1 ${shortLabel(strings, fromId)} = ${formatSig(factor, format)} ${shortLabel(strings, toId)}`;
}

/**
 * A category as it reads inside the "Common … conversions" heading.
 *
 * Prefers the translator's own in-sentence form. The awkward part is telling "this
 * locale wrote one" from "this locale has not reached the key yet": `useToolUi` fills
 * every unset key from the widget's English defaults, so an untranslated locale gets
 * the English word rather than `undefined`, and a plain `??` would put "Conversiones
 * habituales de length" on the Spanish page.
 *
 * So the test is whether the value still IS the English default while the display
 * label has moved on — that pair means the locale is translated but this key is not,
 * and folding its own label is what the page did before this key existed. English
 * itself never takes that branch, because its labels are the defaults.
 *
 * The fold is therefore transitional, not the design. It is wrong for German and Dutch
 * ("Länge" → "länge"), meaningless in Japanese and Chinese, and the `ı`/`i` hazard in
 * Turkish; adding six strings to a locale file removes it for that language for good.
 */
function inSentence(
  strings: typeof UNIT_CONVERTER_STRINGS,
  categoryId: CategoryId
): string {
  const own = strings.categoryLabelsInSentence[categoryId];
  const label = strings.categoryLabels[categoryId];
  const untranslated =
    own === UNIT_CONVERTER_STRINGS.categoryLabelsInSentence[categoryId] &&
    label !== UNIT_CONVERTER_STRINGS.categoryLabels[categoryId];
  return untranslated ? label.toLowerCase() : own;
}

export default function UnitConverterWidget() {
  const strings = useToolUi(UNIT_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [categoryId, setCategoryId] = useState<CategoryId>("length");
  const [value, setValue] = useState("1");
  const [fromId, setFromId] = useState("cm");
  const [toId, setToId] = useState("in");

  const category = findCategory(categoryId);
  const parsed = parseNumberInput(value);
  const valid = parsed !== null;
  const impossible =
    parsed !== null &&
    category.id === "temperature" &&
    isBelowAbsoluteZero(fromId, parsed);
  const result =
    parsed === null || impossible
      ? null
      : convertValue(category, fromId, toId, parsed);

  function handleCategoryChange(next: CategoryId) {
    const def = findCategory(next);
    setCategoryId(next);
    setFromId(def.defaultFrom);
    setToId(def.defaultTo);
  }

  function handleSwap() {
    setFromId(toId);
    setToId(fromId);
  }

  return (
    <>
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.category}
        </p>
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={c.id}
            >
              <input
                checked={categoryId === c.id}
                className="accent-primary"
                name="unit-category"
                onChange={() => handleCategoryChange(c.id)}
                type="radio"
                value={c.id}
              />
              {strings.categoryLabels[c.id]}
            </label>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">
          {strings.baseNotes[categoryId]}
        </p>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="unit-value"
            >
              {strings.value}
            </label>
            <input
              className="w-36 rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="unit-value"
              inputMode="decimal"
              onChange={(e) => setValue(e.target.value)}
              type="number"
              value={value}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="unit-from"
            >
              {strings.from}
            </label>
            <select
              className="rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="unit-from"
              onChange={(e) => setFromId(e.target.value)}
              value={fromId}
            >
              {category.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {unitLabel(strings, u.id)}
                </option>
              ))}
            </select>
          </div>
          <button
            aria-label={strings.swapLabel}
            className="rounded-lg border border-border bg-card px-4 py-3 font-medium text-foreground text-sm transition-colors hover:bg-muted"
            onClick={handleSwap}
            type="button"
          >
            {strings.swap}
          </button>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="unit-to"
            >
              {strings.to}
            </label>
            <select
              className="rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="unit-to"
              onChange={(e) => setToId(e.target.value)}
              value={toId}
            >
              {category.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {unitLabel(strings, u.id)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {result !== null && (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-muted-foreground text-xs">{strings.result}</p>
            <p className="font-mono font-semibold text-2xl text-foreground">
              {formatSig(result, fmt)} {shortLabel(strings, toId)}
            </p>
            <p className="mt-2 text-muted-foreground text-xs">
              {describeConversion(strings, category, fromId, toId, fmt)}
            </p>
          </div>
        )}
        {impossible && (
          <p
            className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm"
            role="alert"
          >
            {strings.belowAbsoluteZero}
          </p>
        )}
        {!valid && value.trim().length > 0 && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {strings.invalid}
          </p>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.commonHeading.replace(
            "{category}",
            inSentence(strings, categoryId)
          )}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b bg-muted/30">
                <th className="p-3 font-medium text-foreground">
                  {strings.headFrom}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.headTo}
                </th>
              </tr>
            </thead>
            <tbody>
              {category.common.map((row) => (
                <tr
                  className="border-border border-b last:border-b-0"
                  key={`${row.value}-${row.from}-${row.to}`}
                >
                  <td className="p-3 font-mono text-foreground">
                    {row.value} {shortLabel(strings, row.from)}
                  </td>
                  <td className="p-3 font-mono text-muted-foreground">
                    {formatSig(
                      convertValue(category, row.from, row.to, row.value),
                      fmt
                    )}{" "}
                    {shortLabel(strings, row.to)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
